import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";



export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [aboutDropdown, setAboutDropdown] = useState(false);

  const mainLinks = [
    { name: "Home", href: "/" },
    { name: "Facilities", href: "/facilities" },
    { name: "Performance", href: "/performance" },
  ];

  const aboutLinks = [
    { name: "About Us", href: "/about" },
    { name: "Founder's Dream", href: "/founders-dream" },
    { name: "Leadership", href: "/leadership" },
    { name: "Faculty", href: "/faculty" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden">
                <img
                  src="/logo.svg"
                  alt="Hilltop Preparatory School Logo"
                  className="w-[120%] h-[120%] object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-slate-900">Hilltop</h1>
                <p className="text-xs text-slate-500 -mt-1">Preparatory School</p>
              </div>
            </Link>


          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {mainLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-slate-700 hover:text-red-600 font-medium transition-colors duration-200"
              >
                {link.name}
              </Link>
            ))}

            {/* About Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setAboutDropdown(true)}
              onMouseLeave={() => setAboutDropdown(false)}
            >
              <button className="flex items-center gap-1 text-slate-700 hover:text-red-600 font-medium transition-colors duration-200">
                About
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${aboutDropdown ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {aboutDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-2 overflow-hidden"
                  >
                    {aboutLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        className="block px-4 py-2 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="mailto:info@hilltop.co.ke?subject=Application%20Request&body=Hello%20Hilltop%20Team,%0D%0A%0D%0AI%20would%20like%20to%20apply."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2.5 rounded-full font-medium hover:shadow-lg hover:shadow-red-500/25 transition-all duration-300"
            >
              Apply Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-slate-700 hover:text-red-600 transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100"
          >
            <div className="px-4 py-4 space-y-1">
              {mainLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block text-slate-700 hover:text-red-600 font-medium py-2 transition-colors"
                >
                  {link.name}
                </Link>
              ))}

              {/* About Section */}
              <div className="py-2">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">
                  About
                </p>
                {aboutLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className="block text-slate-700 hover:text-red-600 font-medium py-2 pl-4 transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              <a
                href="mailto:info@hilltop.co.ke?subject=Application%20Request&body=Hello%20Hilltop%20Team,%0D%0A%0D%0AI%20would%20like%20to%20apply."
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="block bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-full font-medium text-center mt-4"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
